import { Ref, useEffect, useImperativeHandle } from 'react';
import { getDataIdGenerator } from '../../../../shared/utils/dataId';
import { PaginationControls } from '../../../../pagination-controls/PaginationControls';
import { FieldsConfig } from '../FieldsConfig/FieldsConfig';
import { PaginationData } from '../../../../pagination-controls/interfaces';
import { useObjectsConfig } from './hooks/useObjectsConfig';
import { Table } from '../../../../table/Table';
import { ObjectConfig } from '../../interfaces/object';
import { LoadingObjects } from './LoadingObjects';
import { RetryApiAlert } from '../../../../shared/components/RetryApiAlert/RetryApiAlert';
import { TableShimmer } from '../../../../table/TableShimmer';
import { EmptyWidget } from '../../../../shared/components/EmptyWidget/EmptyWidget';
import { Integration } from '../../../../shared/interfaces/integration';
import { SearchArea } from '../../../../input/SearchArea/SearchArea';
import {
  ObjectsConfigImperativeRefState,
  ObjectsConfigInnerState
} from '../interfaces/object-config';
import styles from './ObjectsConfig.module.scss';
import { RefreshSchema } from './RefreshSchema';

export interface ObjectsConfigProps {
  isViewMode: boolean;
  integration: Integration;
  onSave?: VoidFunction;
  formImperativeRef?: Ref<ObjectsConfigImperativeRefState>;
  onStateUpdate?: (state: ObjectsConfigInnerState) => void;
  canRefreshSchema?: boolean;
}

const dataIdGenerator = getDataIdGenerator('objects-config');

export function ObjectsConfig({
  isViewMode,
  integration,
  onSave,
  formImperativeRef,
  onStateUpdate,
  canRefreshSchema
}: ObjectsConfigProps) {
  const {
    objectConfigUiState,
    listConductor,
    fetchObjectsConfigStatus,
    fetchObjectConfig,
    showStatusError,
    showStatusLoader,
    showRetryCTAOnError,
    showObjectsConfigWidget,
    showConfigTable,
    showConfigRetryAlert,
    showEmptyTableWidget,
    showPaginationControls,
    showTableLoadingShimmer,
    disableSubmitCTA,
    submittingRefreshSchemaRequest,
    savingForm,
    objectsConfigFetchErrorMessage,
    statusErrorMessage,
    rows,
    columns,
    objectsTableGlobalContext,
    fieldsSelectionModalConfig,
    onSearch,
    setOpenFieldsSelectionModalConfig,
    onObjectFieldsUpdate,
    onNext,
    onPrev,
    refreshSchema,
    submitForm
  } = useObjectsConfig({
    integration,
    onSave,
    isViewMode
  });

  useImperativeHandle(formImperativeRef, () => ({
    submitForm
  }));

  useEffect(() => {
    if (onStateUpdate) {
      onStateUpdate({
        disabled: disableSubmitCTA,
        savingForm
      });
    }
  }, [disableSubmitCTA, savingForm]);

  return (
    <div className='flex-col h-100 overflow-hidden' data-id={dataIdGenerator('container')}>
      {showObjectsConfigWidget ? (
        <div className='body-container w-100 h-100'>
          <div className='box flex-col h-100'>
            <div className='box__header flex-shrink-0'>
              {!canRefreshSchema ? (
                <span className='box__title'>
                  {isViewMode ? 'Objects' : 'Object Configuration'}
                </span>
              ) : (
                <RefreshSchema
                  refreshSchema={refreshSchema}
                  sourceId={integration.sourceId!}
                  submittingRefreshRequest={submittingRefreshSchemaRequest}
                />
              )}

              <div className='box__header__actions'>
                <SearchArea
                  dataId={dataIdGenerator('')}
                  maxWidth='200px'
                  collapsible={false}
                  autofocus
                  showOperators={false}
                  onSearch={onSearch}
                  debounceInterval={500}
                  placeholder='Search Objects'
                  searchWrapperClassName={styles.searchBar}
                  defaultSearch=''
                />

                {showPaginationControls ? (
                  <>
                    <div className={`${styles.WidgetSeparator} mx-5`} />

                    <PaginationControls
                      nextPage={onNext}
                      prevPage={onPrev}
                      dataId={dataIdGenerator('')}
                      listState={objectConfigUiState}
                      paginationData={listConductor?.getListMeta().pagination as PaginationData}
                      trackingProps={{
                        pipelineId: integration.id,
                        widget: 'ObjectsConfig'
                      }}
                    />
                  </>
                ) : null}
              </div>
            </div>

            <div className='box__body flex-1 overflow-hidden p-0'>
              {showTableLoadingShimmer ? (
                <TableShimmer
                  dataId={dataIdGenerator('tableShimmer')}
                  columnsCount={5}
                  rowsCount={5}
                  tableProps={{ withoutShadow: true, compactHeader: true }}
                />
              ) : null}

              {showConfigTable ? (
                <Table
                  columns={columns}
                  rows={rows}
                  globalContext={objectsTableGlobalContext}
                  withoutShadow
                  compactHeader
                  classes={{ containerClass: 'h-100', headerClass: 'bg-secondary' }}
                  forceFullWidth
                />
              ) : null}

              {showEmptyTableWidget ? (
                <EmptyWidget
                  dataId={dataIdGenerator('')}
                  icon='database'
                  title='No Objects Found'
                  className='h-100 w-100'
                />
              ) : null}

              {showConfigRetryAlert ? (
                <RetryApiAlert
                  dataId={dataIdGenerator('')}
                  title='Error while fetching Objects'
                  subTitle={objectsConfigFetchErrorMessage}
                  trackingProps={{
                    widget: 'ObjectsConfig',
                    error: objectsConfigFetchErrorMessage
                  }}
                  secondaryCTAProps={{
                    onClick: fetchObjectConfig
                  }}
                  className='h-100 w-100'
                />
              ) : null}
            </div>

            <FieldsConfig
              open={fieldsSelectionModalConfig.visible}
              setOpen={setOpenFieldsSelectionModalConfig}
              onApplyChanges={object => {
                onObjectFieldsUpdate(object);
              }}
              integration={integration}
              object={fieldsSelectionModalConfig.object as ObjectConfig}
              isViewMode={isViewMode}
            />
          </div>
        </div>
      ) : null}

      {showStatusLoader ? <LoadingObjects /> : null}

      {showStatusError ? (
        <RetryApiAlert
          dataId={dataIdGenerator('')}
          title='Error while fetching Objects'
          subTitle={statusErrorMessage}
          trackingProps={{
            widget: 'ObjectsConfig',
            error: statusErrorMessage
          }}
          secondaryCTAProps={
            showRetryCTAOnError
              ? {
                  onClick: fetchObjectsConfigStatus
                }
              : undefined
          }
          className='h-100 w-100'
        />
      ) : null}
    </div>
  );
}
